import React from 'react';
import { ProfileManager } from '../../../components/inventory/ProfileManager';
import { InventoryRoastProfileButton } from '../../../components/inventory/InventoryRoastProfileButton';
import { QCStatusButton } from '../../../components/inventory/QCStatusButton';
import type { InventorySample } from '../../../types/inventory';
import { useLanguage } from '../../../features/language/useLanguage';

interface InventorySamplesSectionProps {
  samples: InventorySample[];
  onProfileUpdate: (silent?: boolean) => void;
  onRoastClick: (referenceId: string, label: string) => void;
  getGreenBeanQC: (entityId: string, entityType: 'LotType' | 'SampleType') => boolean | null | undefined;
  onQCClick: (
    type: 'bean' | 'cupping',
    entityId: string,
    entityType: 'LotType' | 'SampleType' | 'ItemType',
    lotId?: string
  ) => void;
  onAddSample: () => void;
  canWrite?: boolean;
}

export const InventorySamplesSection: React.FC<InventorySamplesSectionProps> = ({
  samples,
  onProfileUpdate,
  onRoastClick,
  getGreenBeanQC,
  onQCClick,
  onAddSample,
  canWrite = true,
}) => {
  const { t } = useLanguage();
  return (
    <div data-test-id="inventory-samples-section">
      {samples.length === 0 ? (
        <p className="text-sm text-gray-500 italic" data-test-id="inventory-samples-empty">
          {t('inventory.samples.empty')}
        </p>
      ) : (
        <table className="min-w-full divide-y divide-gray-200 text-sm" data-test-id="inventory-samples-table">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-500">{t('purchasing.sampleList.table.sampleNumber')}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">{t('reports.general.table.date')}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">{t('purchasing.sampleList.table.notes')}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">{t('purchasing.sampleList.table.greenBeanProfile')}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">{t('purchasing.sampleList.table.roastProfile')}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">{t('purchasing.sampleList.table.tasteProfile')}</th>
              <th className="px-4 py-2 font-medium text-center text-gray-500">{t('purchasing.sampleList.table.qualityControl')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {samples.map(sample => (
              <tr key={sample.id} data-test-id={`inventory-sample-row-${sample.id}`}>
                <td className="px-4 py-2 font-medium text-gray-900">{sample.sampleNumber}</td>
                <td className="px-4 py-2 text-gray-500">
                  {new Date(sample.date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </td>
                <td className="px-4 py-2 text-gray-500">{sample.notes || t('settingsMcp.values.none')}</td>
                <td className="px-4 py-2 text-gray-500">
                  {sample.lotId ? (
                    <span className="text-gray-400 text-xs italic">{t('purchasing.sampleList.values.fillProfileInLot')}</span>
                  ) : (
                    <ProfileManager
                      entityType="SampleType"
                      entityId={sample.id}
                      profileType="greenBean"
                      existingProfile={sample.greenBeanProfileId ? { id: sample.greenBeanProfileId } : undefined}
                      onProfileUpdate={() => onProfileUpdate(true)}
                    />
                  )}
                </td>
                <td className="px-4 py-2 text-gray-500">
                  {sample.lotId ? (
                    <span className="text-gray-400 text-xs italic">{t('purchasing.sampleList.values.fillProfileInLot')}</span>
                  ) : (
                    <InventoryRoastProfileButton
                      referenceId={sample.id}
                      dataTestId={`sample-roast-profile-button-${sample.id}`}
                      onClick={() => onRoastClick(sample.id, `Roast Profile – Sample ${sample.sampleNumber}`)}
                      readOnly={!canWrite}
                    />
                  )}
                </td>
                <td className="px-4 py-2 text-gray-500">
                  {sample.lotId ? (
                    <span className="text-gray-400 text-xs italic">{t('purchasing.sampleList.values.fillProfileInLot')}</span>
                  ) : (
                    <ProfileManager
                      entityType="SampleType"
                      entityId={sample.id}
                      profileType="cupping"
                      existingProfile={sample.cuppingProfileId ? { id: sample.cuppingProfileId } : undefined}
                      onProfileUpdate={() => onProfileUpdate(true)}
                    />
                  )}
                </td>
                <td className="px-4 py-2 text-center space-x-2">
                  {sample.lotId ? (
                    <span className="text-gray-400 text-xs italic">{t('purchasing.sampleList.values.fillProfileInLot')}</span>
                  ) : (
                    <>
                      <QCStatusButton
                        type="Bean"
                        status={getGreenBeanQC(sample.id, 'SampleType')}
                        onClick={() => onQCClick('bean', sample.id, 'SampleType', sample.lotId)}
                      />
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canWrite && (
        <div className="flex justify-end mb-4">
          <button
            data-test-id="inventory-add-sample"
            onClick={onAddSample}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium"
          >
            {t('inventory.samples.actions.addSample')}
          </button>
        </div>
      )}
    </div>
  );
};
